/* Krudo shopping-mode switch helper — read/write cart.attributes.mode. */
(function () {
  if (window.KrudoMode) return;

  const MODES = ["nationwide", "local"];

  function readCookie(name) {
    const m = document.cookie.match(
      new RegExp("(?:^|; )" + name.replace(/[.$?*|{}()\[\]\\\/+^]/g, "\\$&") + "=([^;]*)")
    );
    return m ? decodeURIComponent(m[1]) : null;
  }

  async function fetchCart() {
    const r = await fetch("/cart.js", { headers: { Accept: "application/json" } });
    return r.json();
  }

  /* PickEasy persists the chosen fulfillment method as a cart attribute and
     reads it back when it initialises. Writing it here — in the same request as
     the mode, before the reload — means PickEasy builds its calendar from the
     right method's rules on the very first render.

     Getting this wrong is what produced the nationwide slot bug: PickEasy
     defaults to Local Delivery, drew the calendar with local's days, and the
     theme only corrected the selection afterwards by clicking the card. The
     recompute left the already-drawn days behind, so nationwide offered local's
     dates (Wed/Thu/Fri) when it actually had no July slots at all.

     Nationwide has exactly one valid method, so it's pinned. Local has two —
     Local Delivery and Store Pickup — so the attribute is cleared instead of
     pinned, letting PickEasy fall back to its own default while leaving the
     shopper free to pick either. Clearing also avoids clobbering a Store Pickup
     choice made earlier in the session. */
  const FULFILMENT_ATTR = "Order Fulfillment Type";
  const NATIONWIDE_FULFILMENT = "Standard Shipping";

  /* The mode also lives in a cookie, not only in cart.attributes.mode.

     theme.liquid renders data-shopping-mode on <body> only when that cart
     attribute is non-blank, and everything downstream — the card-hiding CSS,
     the selection recovery — keys off the body attribute. Cart attributes are
     tied to the cart, so emptying it (or any clear that drops them) leaves the
     body attribute absent, the CSS matching nothing, and all three fulfillment
     cards on screen. That is the intermittent "sometimes three show".

     The previous theme avoided this entirely by keeping the mode in
     sessionStorage, independent of the cart. A cookie is the same idea and also
     survives a reload, so the mode is knowable before Liquid renders. */
  const MODE_COOKIE = "krudo_mode";

  function writeModeCookie(mode) {
    document.cookie =
      MODE_COOKIE + "=" + encodeURIComponent(mode) + "; path=/; max-age=" + 60 * 60 * 24 * 365;
  }

  function fulfilmentFor(mode) {
    return mode === "nationwide" ? NATIONWIDE_FULFILMENT : "";
  }

  async function writeAttributes(attributes) {
    return fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ attributes }),
    });
  }

  async function writeMode(mode) {
    return writeAttributes({ mode, [FULFILMENT_ATTR]: fulfilmentFor(mode) });
  }

  async function clearCart() {
    return fetch("/cart/clear.js", {
      method: "POST",
      headers: { Accept: "application/json" },
    });
  }

  const KrudoMode = {
    MODES,

    current() {
      return document.body.dataset.shoppingMode || null;
    },

    label(mode) {
      if (mode === "nationwide") return "Nationwide shipping";
      if (mode === "local") return "Local pickup & delivery";
      return "";
    },

    async set(mode, { clearFirst = false, reload = true } = {}) {
      if (!MODES.includes(mode)) throw new Error("Unknown mode: " + mode);
      if (clearFirst) await clearCart();
      await writeMode(mode);
      /* Re-arm: this cart holds the attribute now, so a later render that
         disagrees is a fresh loss and has earned its own reload. */
      clearRestoreGuards();
      document.cookie = "krudo_mode_picked=1; path=/; max-age=" + 60 * 60 * 24 * 365;
      writeModeCookie(mode);
      if (reload) window.location.reload();
    },

    async switchWithConfirm(mode) {
      if (!MODES.includes(mode)) return false;
      const current = this.current();
      if (current === mode) return false;

      const cart = await fetchCart();
      if (cart.item_count > 0 && current && current !== mode) {
        const ok = window.confirm(
          "Switching to " +
            this.label(mode) +
            " will clear your cart, because " +
            this.label(current) +
            " items can't ship that way. Continue?"
        );
        if (!ok) return false;
        await this.set(mode, { clearFirst: true });
        return true;
      }

      await this.set(mode);
      return true;
    },

    /* "Do we know which mode this shopper is in?" — NOT "did they ever pick?"

       This used to accept krudo_mode_picked=1 as proof, and that flag records
       only that a choice happened once, never what it was. krudo_mode_picked
       shipped 2026-05-29; the krudo_mode cookie that carries the actual value
       only shipped 2026-07-29. Everyone who picked in those two months holds a
       year-long flag and no value, and once their cart expired the mode was
       gone for good: the flag suppressed the picker, the missing cookie left
       data-shopping-mode unset, and an unset mode makes syncSwitchControls
       leave the switch button hidden. No modal and no button — no way back in
       without clearing site data.

       So the flag is no longer read. The cookie and the cart attribute are the
       only things that can answer this, and when neither does the honest answer
       is "we do not know", which puts the picker back on screen. It is still
       written on every set() so that older theme versions keep working. */
    hasPicked() {
      const cookie = readCookie(MODE_COOKIE);
      if (cookie && MODES.includes(cookie)) return true;
      return !!this.current();
    },
  };

  window.KrudoMode = KrudoMode;

  /* PickEasy always initialises on Local Delivery. In nationwide that is wrong,
     and because it builds the calendar from whichever method is active at init,
     the wrong default is what produced local's days (Wed/Thu/Fri) on nationwide
     orders. The cart attribute is what overrides it — but only if it is already
     set when PickEasy loads.

     A mode switch covers that: the attribute is written and the page reloads.
     This covers everyone else — a shopper who returns with nationwide already
     stored, a cart that predates this code, or a session where the write didn't
     stick.

     No cart-empty guard, deliberately. The attribute persists on an empty cart
     (verified), and setting it *before* the first item is added is the ideal
     case: PickEasy never initialises on the wrong method at all, so no reload is
     needed. The earlier version skipped empty carts and so missed exactly the
     path a new shopper takes — switch to nationwide, then add an item. */
  const RECONCILED = "krudo_ft_reconciled";

  /* Reconcile data-shopping-mode with the cookie.

     The cookie wins over whatever the HTML says. That is a deliberate
     inversion of the earlier rule, which only filled the attribute in when
     Liquid had left it blank and otherwise trusted the markup.

     Trusting the markup is what stranded shoppers. data-shopping-mode is
     rendered from cart.attributes.mode, so it is only as fresh as the HTML
     holding it, and the HTML in front of a shopper is not always a fresh
     render — a bfcache restore (any Back button press) hands back a DOM
     snapshot taken before the switch, with the old mode baked into <body> and
     the old target baked into every switch control. The cookie, written
     synchronously at click time, is never stale. So it is the source of
     truth and the attribute is corrected to match.

     Returns whether the server's render disagreed with the cookie — i.e.
     whether this page's Liquid-gated content is for the wrong mode. */
  function syncBodyMode() {
    const mode = readCookie(MODE_COOKIE);
    if (!mode || !MODES.includes(mode)) return false;

    /* Compare against what the SERVER rendered, not against the live attribute.

       theme.liquid now stamps the cookie's mode onto <body> inline, one line
       below the opening tag, so the fulfillment CSS is right before anything
       paints instead of a whole document later. That stamp also means the live
       attribute already equals the cookie by the time this runs, so comparing
       the two would report "nothing to fix" on exactly the pages that need
       fixing. The stamp records the pre-overwrite value for this comparison.

       Falling back to the live attribute keeps this correct on any page the
       stamp did not run on. */
    const served =
      typeof window.KrudoServerMode === "string"
        ? window.KrudoServerMode
        : document.body.dataset.shoppingMode || "";

    if (document.body.dataset.shoppingMode !== mode) {
      document.body.dataset.shoppingMode = mode;
    }
    return served !== mode;
  }

  /* One reload per mode per session, to re-render a page the server gated for
     the wrong mode.

     The attribute stamp fixes everything CSS can reach, but it cannot re-gate a
     product grid that Liquid already filtered — on this catalogue that is 128 of
     227 active products, so a nationwide shopper on a stale render is looking at
     a mostly-local catalogue. Only a fresh render fixes that, and only after the
     cart attribute has been written back.

     Capped because the loop is otherwise real: if the cart refuses to hold the
     attribute, every fresh render disagrees with the cookie again and asks for
     another reload. A cap turns that into one wasted reload instead of a pinned
     tab. Storage unavailable counts as "already reloaded" for the same reason —
     without a way to remember, never start. */
  const RESTORE_RELOAD = "krudo_mode_restore_reload";

  function reloadForRestore(mode) {
    try {
      const key = RESTORE_RELOAD + ":" + mode;
      if (sessionStorage.getItem(key)) return false;
      sessionStorage.setItem(key, "1");
    } catch (e) {
      return false;
    }
    window.location.reload();
    return true;
  }

  function clearRestoreGuards() {
    try {
      MODES.forEach((m) => sessionStorage.removeItem(RESTORE_RELOAD + ":" + m));
    } catch (e) {
      /* noop */
    }
  }

  function otherMode(mode) {
    return mode === "nationwide" ? "local" : "nationwide";
  }

  /* Re-point the switch controls at the mode the shopper is NOT in.

     Only the announcement-bar control is relabelled, because it is the only
     one that ships both labels; the rest are left alone so their text can
     never contradict what they do. Their behaviour is corrected at click
     time instead, where the live mode decides the target. */
  function syncSwitchControls() {
    const mode = KrudoMode.current();
    if (!mode) return;
    const target = otherMode(mode);
    document.querySelectorAll("[data-mode-switch]").forEach((el) => {
      const toLocal = el.dataset.labelToLocal;
      const toNationwide = el.dataset.labelToNationwide;
      if (!toLocal || !toNationwide) return;
      el.dataset.modeSwitch = target;
      const label = target === "local" ? toLocal : toNationwide;
      el.setAttribute("aria-label", label);
      const labelEl = el.querySelector("[data-mode-switch-label]");
      if (labelEl) labelEl.textContent = label;
      /* Rendered hidden when Liquid had no mode to render. It does now. */
      el.hidden = false;
    });
  }

  async function reconcileFulfilment() {
    const restored = syncBodyMode();
    syncSwitchControls();

    const mode = KrudoMode.current();
    if (!restored && mode !== "nationwide") return;

    try {
      const cart = await fetchCart();
      if (!cart) return;
      const attrs = cart.attributes || {};

      /* The render disagrees with the cookie. Two different faults underneath
         that, and they need different reloads. */
      if (restored) {
        if (attrs.mode !== mode) {
          /* The cart lost the attribute, so every server render gates the
             catalogue by the wrong mode until it is back. Put it back, then
             re-render — but capped, because if the cart will not hold the
             attribute the next render disagrees again and asks forever. */
          await writeAttributes({ mode });
          if (reloadForRestore(mode)) return;
        } else {
          /* The cart is right and only this HTML is stale — a bfcache snapshot
             taken before the switch. A fresh render is guaranteed to agree with
             the cookie, so this converges on its own and needs no cap; capping
             it would strand the second and third page a shopper walks back
             through. */
          window.location.reload();
          return;
        }
      }

      if (mode !== "nationwide") return;
      if (attrs[FULFILMENT_ATTR] === NATIONWIDE_FULFILMENT) return;

      await writeAttributes({ [FULFILMENT_ATTR]: NATIONWIDE_FULFILMENT });

      /* If no picker exists yet, the write landed before PickEasy initialised
         and there is nothing to correct — much the commoner case now, and it
         costs the shopper nothing. */
      if (!document.querySelector("#order-slots")) return;

      /* A picker is already on screen, so PickEasy read the old value. Only a
         reload makes it re-read. Capped at one per session: a cart that refuses
         to hold the attribute costs a single reload rather than a loop. */
      if (sessionStorage.getItem(RECONCILED)) return;
      sessionStorage.setItem(RECONCILED, "1");
      window.location.reload();
    } catch (e) {
      /* Storage disabled or cart unreachable — nothing safe to do from here. */
    }
  }

  /* Correct the body attribute at once, not only inside the async reconcile —
     the CSS needs it before PickEasy paints its cards, and this script is
     deferred so <body> already exists. */
  if (document.body) syncBodyMode();

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", reconcileFulfilment, { once: true });
  } else {
    reconcileFulfilment();
  }

  /* A bfcache restore replays a DOM snapshot without re-running any of the
     above, so a page the shopper switched away from comes back still showing
     the old mode — old body attribute, old switch target, and a product grid
     the server gated for the mode they have since left. Nothing here can
     re-gate that grid, so when the cookie disagrees the only correct answer is
     a fresh render. It cannot loop: after the reload the cookie and the render
     agree, so the next restore finds nothing to fix. */
  window.addEventListener("pageshow", (event) => {
    if (!event.persisted) return;
    if (syncBodyMode()) {
      /* Through the capped path, not a bare reload. The restore may be a stale
         bfcache snapshot (one reload fixes it) or a cart that has lost the
         attribute (no reload ever fixes it, and a bare one would repeat on every
         Back press). reconcileFulfilment writes the attribute back first, so the
         reload it takes has something to render differently. */
      reconcileFulfilment();
      return;
    }
    syncSwitchControls();
  });

  /* Any [data-mode-switch] element switches modes on click.

     The target comes from the live mode, not from the attribute the server
     stamped on the control. A control rendered before a bfcache restore points
     at the mode the shopper is already in, and switchWithConfirm treats
     "switch to where you already are" as a no-op — so the button did nothing,
     however many times it was pressed, and no amount of clicking could recover
     it. A switch control's job is to switch, so that is what a click does. */
  document.addEventListener("click", (e) => {
    const control = e.target.closest("[data-mode-switch]");
    if (!control) return;
    e.preventDefault();
    const live = KrudoMode.current();
    let mode = control.dataset.modeSwitch;
    if (live && (!mode || mode === live)) mode = otherMode(live);
    if (!mode) return;
    KrudoMode.switchWithConfirm(mode);
  });
})();
